import { Button, CIActionGuard, useUserPermission } from 'ccs-ui-components';
import React from 'react';
import { Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { requiredAuthPermission } from '../../../../common/constants/UserPermissionConstants';
import { type JourneyDesignerServiceReturnResume, saveJourneyDesignerReturnContext } from '../../../../common/utilities/JourneyDesignerReturnContext';
import { AssociatedService, IngressEvent, Service, ServiceOperation } from '../../../store/_api/TypeDefs';
import { DraggingItemType, SidebarDataLoadingState } from '../../../modules/journey-canvas/utility/TypeDefs';
import { getServiceTypeIcon } from '../../journey-builder-footer/utility/Helpers';
import JourneyCanvasListItem from '../../journey-canvas-event-item/JourneyCanvasListItem';

import dragIcon from '../../../assets/images/DragIcon.png';
import classes from './AssociatedServices.module.scss';

interface AssociatedServicesProps {
  hideSidebar: () => void;
  associatedServices: AssociatedService[];
  serviceClickHandler: (service: Service | AssociatedService, type: DraggingItemType, serviceOperation?: ServiceOperation, ingressEvent?: IngressEvent) => void;
  dragStartHandler: (item: Service | AssociatedService, itemType: DraggingItemType, serviceOperation: ServiceOperation, ingressEvent: IngressEvent, ev: React.DragEvent) => void;
  sidebarDataLoadingState: SidebarDataLoadingState;
  isInitialCemService?: boolean;
  journeyName?: string;
  journeyVersion?: number;
  journeyMode?: string;
  stepCardUUID?: string;
  /** Human-readable step name used to round-trip the step in the URL on return. */
  stepName?: string;
  /** Flow item that was highlighted when the user opened this sidebar; restored on return. */
  selectedFlowItemNodeId?: string;
  /** Set when the sidebar is opened from within CEM design; restored on return. */
  cemRootNodeId?: string;
  /** True when the sidebar is rendered inside the CEM editor. */
  designingCEM?: boolean;
}

const AssociatedServices = ({ hideSidebar, associatedServices, serviceClickHandler, dragStartHandler, sidebarDataLoadingState, isInitialCemService = false, journeyName = '', journeyVersion = 0, journeyMode = '', stepCardUUID, stepName, selectedFlowItemNodeId, cemRootNodeId, designingCEM = false }: AssociatedServicesProps) => {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const { permissionObject } = useUserPermission();

  const canEditServices = CIActionGuard(
    [requiredAuthPermission.JD.edit, requiredAuthPermission.JD.approve],
    permissionObject.permission
  );

  const currentReturnPath = window.location.pathname + window.location.search;

  const jdReturnContextBase = () => {
    let serviceReturnResume: JourneyDesignerServiceReturnResume;
    if (designingCEM && stepCardUUID && cemRootNodeId) {
      serviceReturnResume = {
        kind: 'cem',
        stepNodeId: stepCardUUID,
        stepName,
        cemRootNodeId,
        selectedFlowItemNodeId,
      };
    } else if (stepCardUUID) {
      serviceReturnResume = { kind: 'step', stepNodeId: stepCardUUID, stepName, selectedFlowItemNodeId };
    } else {
      serviceReturnResume = { kind: 'none' };
    }
    return {
      returnPath: currentReturnPath,
      journeyName,
      journeyVersion,
      mode: journeyMode,
      serviceReturnResume,
      resumeSidebar: 'ASSOCIATED_SERVICES' as const,
    };
  };

  const navigateToCreateService = () => {
    saveJourneyDesignerReturnContext(jdReturnContextBase());
    navigate('/control-plane/services/createService', { state: { from: currentReturnPath } });
  };

  const navigateToEditService = (service: AssociatedService, e: React.MouseEvent) => {
    e.stopPropagation();
    saveJourneyDesignerReturnContext(jdReturnContextBase());
    navigate(`/control-plane/services/viewServiceDetails/${service.serviceName}/${service.version}/view`, { state: { from: currentReturnPath } });
  };

  return (
    <>
      <div className={classes['sidebar-heading']} data-testid="associatedSideBarHeader">
        <div className={classes['close-sidebar']}>
          <p className={classes['sidebar-title']} data-testid="sidebarTitle">{translate('journey_builder_component.title_service')}</p>
          <Button variant="tertiary" data-testid="sidebarCloseBtn" onClick={hideSidebar}>
            <i className="fa-solid fa-xmark" />
          </Button>
        </div>
        <div className={classes['service-action']}>
          <p>{translate('journey_builder_component.associated_services')}</p>
          {canEditServices && (
            <Button
              variant="primary"
              className={classes['create-service-btn']}
              onClick={navigateToCreateService}
              data-testid="createServiceFromJDAssociated"
            >
              <i className="fa-solid fa-plus" /> {translate('journey_builder_component.btn_create_service')}
            </Button>
          )}
        </div>
      </div>
      {
        sidebarDataLoadingState === SidebarDataLoadingState.NONE && (
          <Col data-testid="associatedSidebar">
            {(associatedServices.length) ? (
              <>
                {associatedServices.filter((s) => s.serviceOperations.length > 0).map((service: AssociatedService) => (
                  <React.Fragment key={`${service.serviceName}_${service.version}`}>
                    {service.serviceOperations.map((operation) => (
                      <React.Fragment key={`${service.serviceName}_${service.version}_${operation.serviceOperation}`}>
                        {operation.ingressEvents.map((event) => (
                          <div className={classes['service-item-wrapper']} key={`${service.serviceName}_${service.version}_${operation.serviceOperation}_${event.ingressEventName}_${event.ingressEventVersion}`}>
                            <JourneyCanvasListItem
                              name={service.serviceName}
                              description={operation.serviceOperation}
                              version={service.version}
                              icon={getServiceTypeIcon(service.serviceType)}
                              onClick={() => serviceClickHandler(service, isInitialCemService ? DraggingItemType.SERVICE : DraggingItemType.ASSOCIATED_SERVICE, isInitialCemService ? undefined : operation, isInitialCemService ? undefined : event)}
                              onDragStart={(ev) => dragStartHandler(service, isInitialCemService ? DraggingItemType.SERVICE : DraggingItemType.ASSOCIATED_SERVICE, operation, event, ev)}
                              disable={false}
                              dragIcon={dragIcon}
                              eventName={event.ingressEventName}
                              eventVersion={event.ingressEventVersion}
                            />
                            {canEditServices && (
                              <Button
                                variant="tertiary"
                                className={classes['edit-service-btn']}
                                onClick={(e) => navigateToEditService(service, e)}
                                data-testid={`editAssociatedService_${service.serviceName}`}
                                title={translate('journey_builder_component.btn_edit_service') ?? ''}
                              >
                                <i className="fa-solid fa-pen-to-square" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ))}
              </>
            ) : (
              <p className={classes['no-data']}>
                {translate('journey_builder_component.txt_no_services')}
              </p>
            )}
          </Col>
        )
      }
    </>
  );
};

export default AssociatedServices;
